/**
 * Shared AI-chatbot visibility gate + open bus.
 *
 * Two surfaces must agree on whether the chatbot exists for the current user:
 * the global FAB (Layout → ChatbotLauncher) and the sidebar "Ask anything…"
 * bar (V3Sidebar). Only the FAB used to ask the backend, so a concessionnaire
 * with `features.ai_chatbot = false` still got a sidebar bar that rendered fine
 * and did nothing when clicked.
 *
 * Three layers, all resolved here so there is one source of truth:
 *   1. Role     — tenants run a scoped portal, sadmins run the control panel.
 *   2. Route    — transactional / setup pages don't benefit from an AI helper.
 *   3. Org      — GET /chatbot/access reports the per-concessionnaire
 *                 entitlement gate (Concessionaire.features.ai_chatbot), which
 *                 the backend also enforces on every /chatbot data route.
 *
 * The org probe is fetched once per concessionnaire and shared through a
 * module-level cache, so mounting both surfaces costs one request. Fail-closed:
 * any error hides the surface rather than showing a control whose every action
 * would 403.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { v3Api } from './useApi';

// Surfaces where the AI chatbot is suppressed. Backend-user landing pages and
// the entire sadmin control panel don't need it — those flows are
// transactional, not analytical. The /admin/settings hub and Tenants page are
// similarly admin-only and don't benefit from an AI helper.
export const CHATBOT_HIDDEN_ROUTES = [
    '/admin/home',
    '/admin/viewapikey',
    '/admin/deletedata',
    '/admin/settings',
    '/admin/tenant',
    '/controlpanel',
];

// Custom event name for "open the chatbot". The sidebar bar and any future
// entry point dispatch it; ChatbotLauncher is the sole listener. Replaces a
// synthetic Ctrl+K KeyboardEvent, which coupled the two files by keystroke and
// silently no-opped whenever the launcher's key handler was gated off.
export const CHATBOT_OPEN_EVENT = 'v3-chatbot-open';

/** Ask the launcher to open. Safe to call from anywhere in the app shell. */
export function openChatbot() {
    window.dispatchEvent(new CustomEvent(CHATBOT_OPEN_EVENT));
}

// Sub-path deploys (homepage=/v3.0, basename="/") carry the PUBLIC_URL prefix on
// location.pathname, so the bare route constants above wouldn't match. Strip it
// once so matching behaves the same as at root. Mirrors Layout.jsx.
function stripPublicUrl(pathname) {
    const prefix = process.env.PUBLIC_URL || '';
    if (prefix && pathname.startsWith(prefix)) return pathname.slice(prefix.length) || '/';
    return pathname;
}

/** Layer 1 — coarse role suppression. Read at call time; usertype is stable per session. */
function isRoleAllowed() {
    const userType = localStorage.getItem('usertype') || '';
    if (userType === 'sadmin' || userType === 'Super Admin') return false;
    if (userType.toLowerCase() === 'tenant') return false;
    return true;
}

/** Layer 2 — route suppression. */
function isRouteAllowed(pathname) {
    const routePath = stripPublicUrl(pathname || '');
    return !CHATBOT_HIDDEN_ROUTES.some(p => routePath === p || routePath.startsWith(p + '/'));
}

// Layer 3 — org entitlement, cached per concessionnaire so switching malls
// (which rewrites localStorage.concessionaireId without a page reload)
// re-probes instead of reusing the previous org's verdict.
let cachedKey = null;
let cachedVerdict = null;
let inflight = null;

function activeOrgKey() {
    return localStorage.getItem('concessionaireId') || '';
}

function probeOrgAccess() {
    const key = activeOrgKey();
    if (cachedKey === key && cachedVerdict !== null) return Promise.resolve(cachedVerdict);
    if (cachedKey === key && inflight) return inflight;

    cachedKey = key;
    cachedVerdict = null;
    inflight = v3Api.chatbot.access()
        .then(res => (res?.data?.enabled === true))
        .catch(() => false)
        .then(verdict => {
            // Ignore a response that landed after the org changed under us.
            if (cachedKey === key) {
                cachedVerdict = verdict;
                inflight = null;
            }
            return verdict;
        });
    return inflight;
}

/**
 * Drop the cached verdict so the next consumer re-probes. Call after an admin
 * flips features.ai_chatbot, or on sign-out.
 */
export function invalidateChatbotAccess() {
    cachedKey = null;
    cachedVerdict = null;
    inflight = null;
}

/**
 * @returns {boolean|null} true = show the chatbot surface, false = hide it,
 *   null = the org probe is still in flight (treat as hidden; kept distinct so
 *   a caller can render a placeholder instead of a flash).
 */
export function useChatbotAccess() {
    const location = useLocation();
    const coarseAllowed = isRoleAllowed() && isRouteAllowed(location.pathname);
    const [orgEnabled, setOrgEnabled] = useState(() =>
        (cachedKey === activeOrgKey() ? cachedVerdict : null));

    useEffect(() => {
        // Don't spend a request on a user/route that can't see the chatbot anyway.
        if (!coarseAllowed) return undefined;
        let alive = true;
        probeOrgAccess().then(verdict => { if (alive) setOrgEnabled(verdict); });
        return () => { alive = false; };
    }, [coarseAllowed]);

    if (!coarseAllowed) return false;
    if (orgEnabled === null) return null;
    return orgEnabled;
}
